import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown, GlobeIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const isIS = language === 'is';
  const serviceLinks = [{
    name: t('navbar.servicesDropdown.overview'),
    path: '/services'
  }, {
    name: isIS ? 'Einstaklingsþjálfun' : '1:1 Coaching',
    path: '/services/coaching'
  }, {
    name: isIS ? 'Innbyggður sérfræðingur' : 'Embedded Specialist',
    path: '/services/embedded-partner'
  }, {
    name: t('navbar.servicesDropdown.courses'),
    path: '/services/courses'
  }, {
    name: t('navbar.servicesDropdown.workshops'),
    path: '/services/workshops'
  }, {
    name: t('navbar.servicesDropdown.talks'),
    path: '/services/talks'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');
  const isEventsActive = () => location.pathname === '/events';
  const isWritingActive = () => location.pathname.startsWith('/writing');
  const toggleLanguage = () => setLanguage(language === 'is' ? 'en' : 'is');
  return <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-121">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-122">
        <div className="flex justify-between items-center h-20" data-id="element-123">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-124">
            <span className="font-signature text-3xl text-teal" data-id="element-125">HelgiG</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-126">
            <Link to="/" className={`relative font-medium transition-colors hover:text-coral ${isActive('/') ? 'text-coral' : 'text-gray-600'}`} data-id="element-127">
              {t('navbar.home')}
              {isActive('/') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-128" />}
            </Link>

            <Link to="/about" className={`relative font-medium transition-colors hover:text-coral ${isActive('/about') ? 'text-coral' : 'text-gray-600'}`} data-id="element-129">
              {t('navbar.about')}
              {isActive('/about') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-130" />}
            </Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-131">
              <Link to="/services" className={`relative font-medium transition-colors hover:text-coral flex items-center gap-1 ${isServicesActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-132">
                {t('navbar.services')}
                <ChevronDown size={16} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-133" />
                {isServicesActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-134" />}
              </Link>

              <AnimatePresence data-id="element-135">
                {servicesOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15
              }} className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 overflow-hidden" data-id="element-136">
                    {serviceLinks.map((link, i) => <React.Fragment key={link.path} data-id="element-137">
                        {i === 3 && <div className="border-t border-gray-100 my-1" data-id="element-138" />}
                        <Link to={link.path} className={`block px-4 py-2.5 text-sm font-medium transition-colors ${isActive(link.path) ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50 hover:text-coral'}`} data-id="element-139">
                          {link.name}
                        </Link>
                      </React.Fragment>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/events" className={`relative font-medium transition-colors hover:text-coral ${isEventsActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-140">
              {isIS ? 'Viðburðir' : 'Events'}
              {isEventsActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-141" />}
            </Link>

            {/* Writing */}
            <Link to="/writing" className={`relative font-medium transition-colors hover:text-coral ${isWritingActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-142">
              {isIS ? 'Skrif' : 'Writing'}
              {isWritingActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-143" />}
            </Link>

            <Link to="/contact" className={`relative font-medium transition-colors hover:text-coral ${isActive('/contact') ? 'text-coral' : 'text-gray-600'}`} data-id="element-144">
              {t('navbar.contact')}
              {isActive('/contact') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-145" />}
            </Link>

            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-146">
              <GlobeIcon size={16} data-id="element-147" />
              <span data-id="element-148">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>

            <Link to="/contact" data-id="element-149">
              <Button size="sm" variant="secondary" data-id="element-150">{t('navbar.bookNow')}</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3" data-id="element-151">
            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-152">
              <GlobeIcon size={14} data-id="element-153" />
              <span data-id="element-154">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-coral transition-colors" data-id="element-155">
              {isOpen ? <X size={28} data-id="element-156" /> : <Menu size={28} data-id="element-157" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-158">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-159">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-160">
              <Link to="/" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-161">
                {t('navbar.home')}
              </Link>
              <Link to="/about" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/about') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-162">
                {t('navbar.about')}
              </Link>

              <div data-id="element-163">
                <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium ${isServicesActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-164">
                  {t('navbar.services')}
                  <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-165" />
                </button>
                {mobileServicesOpen && <div className="pl-4 pt-1 space-y-1" data-id="element-166">
                    {serviceLinks.map((link, i) => <React.Fragment key={link.path} data-id="element-167">
                        {i === 3 && <div className="border-t border-gray-100 my-1" data-id="element-168" />}
                        <Link to={link.path} onClick={() => {
                  setIsOpen(false);
                  setMobileServicesOpen(false);
                }} className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${isActive(link.path) ? 'bg-coral/5 text-coral' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-169">
                          {link.name}
                        </Link>
                      </React.Fragment>)}
                  </div>}
              </div>

              <Link to="/events" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isEventsActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-170">
                {isIS ? 'Viðburðir' : 'Events'}
              </Link>

              <Link to="/writing" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isWritingActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-171">
                {isIS ? 'Skrif' : 'Writing'}
              </Link>

              <Link to="/contact" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/contact') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-172">
                {t('navbar.contact')}
              </Link>

              <div className="pt-2 px-4" data-id="element-173">
                <Link to="/contact" onClick={() => setIsOpen(false)} data-id="element-174">
                  <Button className="w-full" variant="secondary" data-id="element-175">{t('navbar.bookNow')}</Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}